import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { VideoCamera, Lightning, FilmStrip, Sparkle, MagicWand, DownloadSimple, Plus, FolderOpen, Check, CircleNotch, MagnifyingGlass, Microphone, X, Copy, CaretDown, CaretRight } from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";
import { downloadFromUrl } from "@/components/shared";

const TYPE_META = {
  auto_create: { label: "Auto Create", color: "bg-[#E91E8C]/10 text-[#E91E8C] border-[#E91E8C]/30", icon: Sparkle },
  lifestyle: { label: "Lifestyle", color: "bg-amber-50 text-amber-700 border-amber-200", icon: FilmStrip },
  motion: { label: "Motion", color: "bg-violet-50 text-violet-700 border-violet-200", icon: MagicWand },
  base_video: { label: "Base Library", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: VideoCamera },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "auto_create", label: "Auto Create" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "motion", label: "Motion" },
  { id: "base_video", label: "Base Library" },
];

export default function AllMyVideos() {
  const [videos, setVideos] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());
  const [error, setError] = useState("");
  const [remixTarget, setRemixTarget] = useState(null); // video being remixed
  const [twins, setTwins] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (id) => setExpandedIds((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/my-videos`);
      setVideos(res.data.videos || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load videos");
    } finally {
      setLoading(false);
    }
  };

  const fetchTwins = async () => {
    try {
      const r = await axios.get(`${API}/ai-twin/images`);
      setTwins(r.data.images || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { fetchAll(); fetchTwins(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return videos.filter((v) => {
      if (filter !== "all" && v.type !== filter) return false;
      if (needle && !(v.title || "").toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [videos, filter, q]);

  const handleDownload = (v) => {
    const url = v.playback_url?.startsWith("/api/")
      ? `${BACKEND_URL}${v.playback_url}`
      : (v.video_url || v.playback_url);
    downloadFromUrl(url, `${(v.title || "video").slice(0, 40).replace(/[^a-z0-9]/gi, "_")}.mp4`);
  };

  const handleSaveToVault = async (v) => {
    try {
      const url = v.playback_url?.startsWith("/api/")
        ? `${BACKEND_URL}${v.playback_url}`
        : v.video_url;
      // VaultItem expects hashtags + tags as single strings (space / comma separated)
      const hashtagsStr = Array.isArray(v.hashtags)
        ? v.hashtags.join(" ")
        : (v.hashtags || "");
      const tagsStr = [v.type, v.subtype || ""].filter(Boolean).join(",");
      await axios.post(`${API}/vault/save`, {
        title: v.title,
        platform: "tiktok",
        category: v.type,
        video_url: url,
        caption: v.caption || "",
        hashtags: hashtagsStr,
        tags: tagsStr,
        post_type: "video",
        scheduled_date: null,
        source_job_id: v.id,
      });
      setSavedIds((prev) => new Set([...prev, v.id]));
      setTimeout(() => setSavedIds((prev) => { const n = new Set(prev); n.delete(v.id); return n; }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save to vault");
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      setError(""); // clear any previous error
      // Ephemeral flash in place of a toast
      const prev = error;
      setError(`✓ ${label} copied`);
      setTimeout(() => setError((e) => (e === `✓ ${label} copied` ? prev : e)), 1500);
    } catch {
      setError(`Couldn't copy ${label}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="all-my-videos">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">All My Videos</h1>
        <p className="text-sm text-zinc-500 mt-1">Every completed video in one place · {counts.total || 0} total</p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => {
          const n = f.id === "all" ? counts.total : counts[f.id];
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                filter === f.id
                  ? "bg-[#E91E8C] text-white"
                  : "bg-white border border-zinc-200 text-zinc-600 hover:border-[#E91E8C]/30 hover:text-[#E91E8C]"
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}{typeof n === "number" ? ` · ${n}` : ""}
            </button>
          );
        })}
        <div className="flex-1" />
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles..."
            className="pl-8 pr-3 py-1.5 rounded-full border border-zinc-200 text-xs bg-white focus:border-[#E91E8C] focus:ring-2 focus:ring-[#E91E8C]/20 outline-none min-w-[200px]"
            data-testid="all-videos-search"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#E91E8C]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-zinc-400">
          <VideoCamera size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">{q ? "No matches for your search." : "No videos yet — create one from Auto Create, Lifestyle, or Motion Control."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((v) => {
            const meta = TYPE_META[v.type] || TYPE_META.base_video;
            const Icon = meta.icon;
            const playback = v.playback_url?.startsWith("/api/") ? `${BACKEND_URL}${v.playback_url}` : (v.video_url || v.playback_url);
            const saved = savedIds.has(v.id);
            return (
              <div key={`${v.type}-${v.id}`} className="card overflow-hidden group" data-testid={`video-card-${v.id}`}>
                <div className="relative aspect-[9/16] bg-black">
                  <video src={playback} controls preload="metadata" playsInline className="w-full h-full object-cover" />
                  <span className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.color} backdrop-blur-sm flex items-center gap-1`}>
                    <Icon size={9} weight="fill" /> {meta.label}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-sm font-medium text-zinc-900 truncate" title={v.title}>{v.title}</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {v.pipeline_mode === "lip_sync" && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                        <VideoCamera size={9} weight="fill" /> Lip Sync
                      </span>
                    )}
                    {v.pipeline_mode === "talking_photo" && v.talking_photo_mode && (
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        v.talking_photo_mode === "realistic" ? "bg-[#E91E8C]/10 text-[#E91E8C] border-[#E91E8C]/30" : "bg-amber-100 text-amber-700 border-amber-200"
                      } flex items-center gap-1`}>
                        {v.talking_photo_mode === "realistic" ? "Realistic" : <><Lightning size={9} weight="fill" />Prompted</>}
                      </span>
                    )}
                    {v.voice_label && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200 flex items-center gap-1">
                        <Microphone size={9} weight="fill" /> {v.voice_label}
                      </span>
                    )}
                    {v.created_at && (
                      <span className="text-[9px] text-zinc-400 ml-auto">
                        {new Date(v.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  {(v.caption || v.hashtags) && (() => {
                    const open = expandedIds.has(v.id);
                    const hashtagsStr = Array.isArray(v.hashtags) ? v.hashtags.join(" ") : (v.hashtags || "");
                    const fullCaption = [v.caption, hashtagsStr].filter(Boolean).join("\n\n");
                    return (
                      <div className="border border-zinc-100 bg-zinc-50/50 rounded-md">
                        <button
                          onClick={() => toggleExpanded(v.id)}
                          className="w-full flex items-center gap-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600 hover:text-[#E91E8C]"
                          data-testid={`video-caption-toggle-${v.id}`}
                        >
                          {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                          Caption + hashtags
                          <span className="ml-auto normal-case font-normal text-zinc-400">{hashtagsStr.split(/\s+/).filter(Boolean).length} tags</span>
                        </button>
                        {open && (
                          <div className="px-2 pb-2 pt-1 space-y-2">
                            {v.caption && (
                              <div className="flex items-start gap-1.5">
                                <p className="text-[11px] text-zinc-700 leading-snug flex-1 whitespace-pre-wrap">{v.caption}</p>
                                <button
                                  onClick={() => copyToClipboard(v.caption, "Caption")}
                                  className="shrink-0 p-1 text-zinc-400 hover:text-[#E91E8C]"
                                  title="Copy caption"
                                  data-testid={`video-caption-copy-${v.id}`}
                                >
                                  <Copy size={11} weight="bold" />
                                </button>
                              </div>
                            )}
                            {hashtagsStr && (
                              <div className="flex items-start gap-1.5">
                                <p className="text-[11px] text-[#E91E8C] leading-snug flex-1 break-words">{hashtagsStr}</p>
                                <button
                                  onClick={() => copyToClipboard(hashtagsStr, "Hashtags")}
                                  className="shrink-0 p-1 text-zinc-400 hover:text-[#E91E8C]"
                                  title="Copy hashtags"
                                  data-testid={`video-hashtags-copy-${v.id}`}
                                >
                                  <Copy size={11} weight="bold" />
                                </button>
                              </div>
                            )}
                            <button
                              onClick={() => copyToClipboard(fullCaption, "Caption + hashtags")}
                              className="w-full text-[10px] font-semibold uppercase tracking-wider text-zinc-600 hover:text-[#E91E8C] border border-zinc-200 hover:border-[#E91E8C]/40 rounded py-1 flex items-center justify-center gap-1"
                              data-testid={`video-copy-all-${v.id}`}
                            >
                              <Copy size={10} weight="bold" /> Copy all
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-1 pt-1">
                    <button
                      onClick={() => handleDownload(v)}
                      className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-700 hover:text-[#E91E8C] border border-zinc-200 hover:border-[#E91E8C]/40 rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors"
                      data-testid={`video-download-${v.id}`}
                    >
                      <DownloadSimple size={10} weight="bold" /> Download
                    </button>
                    <button
                      onClick={() => handleSaveToVault(v)}
                      disabled={saved}
                      className={`flex-1 text-[10px] font-semibold uppercase tracking-wider border rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors ${
                        saved
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                          : "text-zinc-700 hover:text-[#E91E8C] border-zinc-200 hover:border-[#E91E8C]/40"
                      }`}
                      data-testid={`video-vault-${v.id}`}
                    >
                      {saved ? <><Check size={10} weight="bold" /> Saved</> : <><Plus size={10} weight="bold" /> Vault</>}
                    </button>
                  </div>
                  {v.type !== "motion" && (
                    <button
                      onClick={() => setRemixTarget(v)}
                      className="w-full text-[10px] font-semibold uppercase tracking-wider text-violet-700 hover:text-white bg-violet-50 hover:bg-violet-600 border border-violet-200 hover:border-violet-600 rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors"
                      data-testid={`video-remix-${v.id}`}
                    >
                      <MagicWand size={10} weight="fill" /> Motion Remix
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {remixTarget && (
        <MotionRemixModal
          video={remixTarget}
          twins={twins}
          onClose={() => setRemixTarget(null)}
          onSubmitted={() => {
            setRemixTarget(null);
            // Refresh after a short delay so the new motion job shows up under the Motion filter
            setTimeout(fetchAll, 1500);
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Motion Remix Modal — pick a twin + model + aspect ratio, then POST
// /motion-control/remix which downloads the source, saves a motion_ref,
// and kicks off a transfer in one round-trip.
// ──────────────────────────────────────────────────────────────────
const MODELS = [
  { id: "runway-act-two-i2v", label: "Runway Act-Two — fastest, facial + head motion", supportsAspect: true },
  { id: "kling-v2.6-pro-motion-control", label: "Kling 2.6 Pro — V2V camera + subject", supportsAspect: false },
  { id: "kling-v3.0-pro-motion-control", label: "Kling 3.0 Pro — best quality (slower)", supportsAspect: false },
];

function MotionRemixModal({ video, twins, onClose, onSubmitted }) {
  const [twinId, setTwinId] = useState(twins[0]?.id || "");
  const [model, setModel] = useState("runway-act-two-i2v");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const modelCfg = MODELS.find((m) => m.id === model) || MODELS[0];

  const handleSubmit = async () => {
    if (!twinId) { setError("Pick an AI Twin first."); return; }
    setSubmitting(true); setError("");
    try {
      const sourceUrl = video.playback_url?.startsWith("/api/")
        ? `${BACKEND_URL}${video.playback_url}`
        : (video.video_url || video.playback_url);
      const payload = {
        source_video_url: sourceUrl,
        source_name: `Remix · ${(video.title || "clip").slice(0, 60)}`,
        image_id: twinId,
        model,
        prompt: prompt.trim(),
      };
      if (modelCfg.supportsAspect) payload.aspect_ratio = aspectRatio;
      await axios.post(`${API}/motion-control/remix`, payload);
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't start motion remix.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="motion-remix-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
              <MagicWand size={18} weight="fill" className="text-violet-600" /> Motion Remix
            </h2>
            <p className="text-xs text-zinc-500 mt-1">Use "{video.title}" as the driving clip — the motion will be transferred onto your AI Twin.</p>
          </div>
          <button onClick={onClose} className="p-1 text-zinc-400 hover:text-zinc-700" data-testid="remix-close-btn">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-2">1. Pick an AI Twin</label>
          {twins.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">No AI Twin images yet — create one in the AI Twin Gallery first.</p>
          ) : (
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
              {twins.slice(0, 24).map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setTwinId(img.id)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    twinId === img.id ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`remix-twin-${img.id}`}
                >
                  <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">2. Model</label>
          <select value={model} onChange={(e) => setModel(e.target.value)} className="input-field text-sm" data-testid="remix-model">
            {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>

        {modelCfg.supportsAspect && (
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">3. Aspect Ratio</label>
            <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="input-field text-sm" data-testid="remix-aspect">
              <option value="9:16">9:16 — Reels / TikTok / Shorts</option>
              <option value="16:9">16:9 — YouTube / landscape</option>
              <option value="1:1">1:1 — Square</option>
              <option value="4:3">4:3</option>
              <option value="3:4">3:4</option>
              <option value="21:9">21:9 — Ultra-wide</option>
            </select>
          </div>
        )}

        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Prompt (optional)</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
            placeholder="e.g. cinematic lighting, warm background, confident posture"
            className="input-field text-sm resize-none"
            data-testid="remix-prompt"
          />
        </div>

        {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2.5">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-zinc-200 text-zinc-700 text-sm font-semibold hover:bg-zinc-50"
            data-testid="remix-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !twinId || twins.length === 0}
            className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="remix-submit-btn"
          >
            {submitting ? <><CircleNotch size={14} className="animate-spin" /> Starting…</> : <><MagicWand size={14} weight="fill" /> Start Remix</>}
          </button>
        </div>
      </div>
    </div>
  );
}
